import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { registerUser } from '../services/authService';
import { toast } from 'react-toastify';
import '../styles/Auth.css';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    betaAccessCode: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (!formData.betaAccessCode.trim()) {
      toast.error('Beta access code is required');
      return;
    }

    // Additional validation for beta access code format
    if (formData.betaAccessCode.trim().length < 3) {
      toast.error('Beta access code must be at least 3 characters long');
      return;
    }

    setIsLoading(true);
    
    try {
      const data = await registerUser(
        formData.username, 
        formData.email, 
        formData.password, 
        formData.betaAccessCode.trim()
      );
      
      if (data.access) {
        localStorage.setItem('access_token', data.access);
        login(data);
        toast.success('Account created successfully!');
        navigate('/');
      } else {
        toast.error(data.error || 'Failed to create account');
      }
    } catch (error) {
      // Handle specific beta access code errors
      if (error.message.includes('Invalid beta access code')) {
        toast.error('Invalid beta access code. Please check your code and try again.');
      } else if (error.message.includes('Beta access code is required')) {
        toast.error('Beta access code is required to create an account.');
      } else {
        toast.error(error.message || 'Failed to create account');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        {/* Logo Section */}
        <div className="logo-container">
          <img src="/images/logo48.png" alt="Sighed Kick Logo" className="auth-logo" />
          <h1 className="auth-title">Sighed Kick</h1>
        </div>
        
        <h2>Create Account</h2>
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="betaAccessCode">Beta Access Code</label>
            <input
              type="text"
              id="betaAccessCode"
              name="betaAccessCode"
              value={formData.betaAccessCode}
              onChange={handleChange}
              required
              placeholder="Enter your beta access code"
              className="form-input"
            />
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>
        
        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <button 
              onClick={() => navigate('/login')}
              className="auth-link-button"
            >
              Log In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
